/**
 * Elite Realty 05 — Halves, joined by a ticket.
 *
 * Cut straight across the middle: washed photograph above, navy below, and a
 * gold TICKET straddling the join — notched at both ends, hatched on its stub,
 * carrying the price and the viewing time. The lower half runs a dot field
 * behind a feature column, layered interiors on cards, and a shadowed QR plate.
 */

import { CANVAS, SPACE, RULE, RADIUS, SHADOW } from '../system/tokens';
import { getPalette, scrim } from '../system/palettes';
import { roles } from '../system/typography';
import { grid } from '../system/grid';
import { text, shape, rule, image, qrcode, page } from '../system/elements';
import { getIcon } from '../system/icons';
import { PHOTOS } from '../system/photos';
import { BRAND, SPECS, FEATURES } from './system';
import { card, iconDisc, dotGrid, hatch, wash, layeredPhotos } from './decor';

const ID = 'tpl-elite-flyer-05';

export default {
  id: ID,
  meta: {
    name: 'Elite — Your Dream Home',
    collection: 'elite-realty',
    archetype: 'halves-with-ticket',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'elite-navy-gold',
    pairing: 'elite-signature',
    tags: ['real estate', 'flyer', 'open house', 'ticket', 'invitation', 'navy', 'gold', 'us letter'],
    description: 'The sheet cut across the middle — washed photograph above, navy below — joined by a notched gold ticket carrying the price and viewing time.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('elite-navy-gold');
    const r = roles('elite-signature');
    const g = grid(canvas, { margin: 165 });
    const els = [];

    const SPLIT = 1380;

    // ── Upper half ──────────────────────────────────────────────────────────
    els.push(image(`${ID}-hero`, { x: 0, y: 0, width: canvas.width, height: SPLIT, src: PHOTOS.terracottaVilla }));
    els.push(wash(`${ID}-wash-top`, { x: 0, y: 0, width: canvas.width, height: 480, color: p.bg, angle: 0, from: 0, to: 0.76 }));
    els.push(wash(`${ID}-wash-bot`, { x: 0, y: SPLIT - 420, width: canvas.width, height: 420, color: p.bg, to: 0.75 }));

    els.push(...iconDisc(`${ID}-logo`, {
      x: g.left, y: 150, size: 96, content: getIcon('roofMark'), discFill: p.accent, iconColor: p.accentInk,
    }));
    els.push(text(`${ID}-logo-name`, r.h3, `${BRAND.name}  ${BRAND.sub}`, { x: g.left + 126, y: 172, width: 1000 },
      { color: p.ink, fontSize: 44, letterSpacing: 10 }));

    els.push(text(`${ID}-eyebrow`, r.statLabel, 'Open house · Saturday', {
      x: g.left, y: SPLIT - 320, width: 1400,
    }, { color: p.accent, fontSize: 34 }));
    els.push(text(`${ID}-addr-top`, r.h3, `${BRAND.address[0]}, ${BRAND.address[1]}`, {
      x: g.left, y: SPLIT - 262, width: 1600,
    }, { color: p.ink, fontSize: 52, letterSpacing: 2 }));

    // ── Lower half ──────────────────────────────────────────────────────────
    els.push(shape(`${ID}-lower`, { x: 0, y: SPLIT, width: canvas.width, height: canvas.height - SPLIT, fill: p.bg }));
    els.push(dotGrid(`${ID}-dots`, {
      x: g.left, y: 2760, width: 520, height: 250, color: scrim(p.ink, 0.32), step: 34, radius: 6,
    }));

    // ── The ticket, straddling the join ─────────────────────────────────────
    const TW = 2120;
    const TH = 340;
    const TX = Math.round((canvas.width - TW) / 2);
    const TY = SPLIT - Math.round(TH / 2);
    const STUB = TX + 1420;

    els.push(...card(`${ID}-ticket`, {
      x: TX, y: TY, width: TW, height: TH, fill: p.accent, radius: RADIUS.sm, shadow: SHADOW.card,
    }));
    els.push(hatch(`${ID}-stub-hatch`, {
      x: STUB + 8, y: TY, width: 46, height: TH, color: scrim(p.accentInk, 0.3), step: 18, weight: 6,
    }));
    els.push(shape(`${ID}-notch-l`, { x: TX - 32, y: TY + TH / 2 - 32, width: 64, height: 64, kind: 'circle', fill: p.bg }));
    els.push(shape(`${ID}-notch-r`, { x: TX + TW - 32, y: TY + TH / 2 - 32, width: 64, height: 64, kind: 'circle', fill: p.bg }));
    els.push(shape(`${ID}-perf`, { x: STUB, y: TY + 36, width: RULE.thin, height: TH - 72, fill: scrim(p.accentInk, 0.45) }));

    els.push(text(`${ID}-t-label`, r.statLabel, 'Offered at', { x: TX + SPACE.lg, y: TY + 58, width: 900 },
      { color: p.accentInk, fontSize: 32 }));
    els.push(text(`${ID}-t-price`, r.h1, '$789,000', { x: TX + SPACE.lg, y: TY + 110, width: 1160 },
      { color: p.accentInk, fontSize: 132 }));
    els.push(text(`${ID}-t-when`, r.statLabel, 'Open Saturday', { x: STUB + SPACE.md, y: TY + 78, width: 540 },
      { color: p.accentInk, fontSize: 28 }));
    els.push(text(`${ID}-t-time`, r.h3, '11 – 2', { x: STUB + SPACE.md, y: TY + 128, width: 540 },
      { color: p.accentInk, fontSize: 78 }));

    // ── Lower half, left column ─────────────────────────────────────────────
    let y = TY + TH + SPACE.xl;
    const COLW = 1120;

    els.push(text(`${ID}-script`, r.script, 'Your dream home', { x: g.left + 8, y, width: COLW },
      { color: p.accent, fontSize: 112, overlapOk: true }));
    y += 112 * 0.74;

    const headline = text(`${ID}-headline`, r.display, 'AWAITS', { x: g.left, y, width: COLW },
      { color: p.ink, fontSize: 178 });
    els.push(headline);
    y += headline.height + SPACE.sm;

    els.push(rule(`${ID}-rule`, { x: g.left, y, width: 260, weight: RULE.medium, color: p.accent }));
    y += RULE.medium + SPACE.md;

    SPECS.forEach((s, i) => {
      const ry = y + i * 104;
      els.push(...iconDisc(`${ID}-spec${i}`, {
        x: g.left, y: ry, size: 68, content: getIcon(s.icon),
        discFill: scrim(p.ink, 0.1), iconColor: p.accent,
      }));
      els.push(text(`${ID}-spec${i}`, r.h3, `${s.value}  ${s.label}`, {
        x: g.left + 92, y: ry + 8, width: COLW - 92,
      }, { color: p.ink, fontSize: 44 }));
    });

    // ── Lower half, right column ────────────────────────────────────────────
    const RX = g.left + COLW + SPACE.lg;
    const RW = g.right - RX;
    let ry = TY + TH + SPACE.xl;

    els.push(...layeredPhotos(`${ID}-gallery`, {
      x: RX, y: ry, width: RW, height: 460,
      main: PHOTOS.openPlanLiving, inset: PHOTOS.brassBedroom, insetScale: 0.4,
      cardColor: p.surface, accentColor: p.accent,
    }));
    ry += 460 + SPACE.xl;

    els.push(text(`${ID}-feat-head`, r.statLabel, 'Worth noting', { x: RX, y: ry, width: RW },
      { color: p.accent, fontSize: 32 }));
    ry += 60;
    FEATURES.slice(0, 5).forEach((f, i) => {
      const fy = ry + i * 62;
      els.push(shape(`${ID}-feat${i}-dot`, {
        x: RX, y: fy + 16, width: 14, height: 14, kind: 'circle', fill: p.accent,
      }));
      els.push(text(`${ID}-feat${i}`, r.body, f, { x: RX + 38, y: fy, width: RW - 38 },
        { color: p.inkMuted, fontSize: 34 }));
    });

    // ── Sign-off: QR on a plate, contact line ──────────────────────────────
    const QY = canvas.height - 400;
    els.push(...card(`${ID}-qr-card`, {
      x: RX, y: QY, width: 220, height: 220, fill: p.surface, radius: RADIUS.sm, shadow: SHADOW.lift,
    }));
    els.push(qrcode(`${ID}-qr`, { x: RX + 22, y: QY + 22, size: 176, data: BRAND.tourUrl, color: p.bg, bgColor: '#FFFFFF' }));
    els.push(text(`${ID}-qr-cap`, r.statLabel, 'Scan for the\nvirtual tour', { x: RX + 254, y: QY + 60, width: RW - 254 },
      { color: p.accent, fontSize: 30 }));

    els.push(rule(`${ID}-foot-rule`, {
      x: g.left, y: canvas.height - 220, width: g.contentWidth, weight: RULE.hair, color: scrim(p.ink, 0.35),
    }));
    els.push(text(`${ID}-contact`, r.body, `${BRAND.agent}  ·  ${BRAND.phone}  ·  ${BRAND.web}`, {
      x: g.left, y: canvas.height - 172, width: g.contentWidth,
    }, { color: p.inkMuted, fontSize: 36 }));

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.bg, elements: els })],
    };
  },
};
